<template>
  <section>
    <v-row
      class="my-8 flex-column"
      v-if="!imageStore.hasImages() && !searchStore.isSearching && searchStore.anySearchMade"
    >
      <v-col class="text-center">
        <h2 class="title">{{ $t('home.images.noImagesFound') }} 😢</h2>
        <h2 class="title">{{ $t('home.images.noImagesFoundText') }}</h2>
        <br />
        <p v-html="$t('home.images.contactUsIfMissing')"></p>
      </v-col>
    </v-row>

    <v-row row wrap align-content-start ref="imageContainer">
      <v-col d-flex xs="12" sm="6" md="4" lg="2" v-for="image in imageStore.images" :key="image.id">
        <v-card flex>
          <v-img
            :src="image.smallThumbnailPath"
            v-on:click="imageClick(image)"
            contain
            class="thumb-image"
            height="200px"
          >
            <template v-slot:default>
              <div class="pa-3" v-if="image.show_new_product_badge">
                <v-chip class="primary">{{ $t('home.images.newProduct') }}</v-chip>
              </div>
            </template>
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey"></v-progress-circular>
              </v-row>
            </template>
          </v-img>

          <v-card-actions class="d-flex">
            <v-btn color="primary darken-1" outlined class="flex-grow-1" @click="imageClick(image)">{{
              $t('home.images.info')
            }}</v-btn>
            <v-tooltip bottom :disabled="!enableDownloadImageTooltip(image)">
              <template v-slot:activator="{ on, attr }">
                <div v-on="on" v-bind="attr" class="d-flex flex-grow-1 ml-3">
                  <v-btn
                    color="primary darken-1"
                    outlined
                    class="flex-grow-1"
                    @click="downloadImage(image)"
                    :disabled="!enabledDownloadButton(image)"
                    >{{ $t('home.images.download') }}</v-btn
                  >
                </div>
              </template>
              <span v-if="isAuthenticated && !isAuthenticatedJournalist">{{
                $t('home.images.onlyJournalistTooltip')
              }}</span>
              <span v-else v-html="$t('home.images.missingCountryTooltip')"></span>
            </v-tooltip>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="justify-center">
      <v-btn
        color="primary"
        class="black--text mt-2"
        large
        @click="searchStore.nextPage()"
        v-if="!searchStore.isOnLastPage"
        :loading="searchStore.isSearching"
        >{{ $t('home.images.loadMoreImages') }}</v-btn
      >
    </v-row>

    <image-dialog :visible="showImageDialog" :image="selectedImage" @close="hideImageDialog"></image-dialog>

    <login-dialog
      :visible="showLoginDialog"
      :download="true"
      @success="downloadImage()"
      @close="hideLoginDialog"
    ></login-dialog>

    <missing-image-info-snackbar
      :show="!imageStore.hasImages && searchStore.anySearchMade"
    ></missing-image-info-snackbar>

    <info-dialog-component v-model="showMissingCountryInfo" :title="$t('home.images.missingCountryInfoTitle')">
      <p>{{ $t('home.images.missingCountryInfoContent') }} {{ countriesForSelectedImage }}</p>
    </info-dialog-component>
    <info-dialog-component v-model="showNotJournalistInfo" :title="$t('home.images.onlyJournalistInfoTitle')">
      <p>{{ $t('home.images.onlyJournalistInfoContent') }}</p>
    </info-dialog-component>
  </section>
</template>

<script>
import { mapStores } from 'pinia';
import { useImageStore } from '../../../stores/ImageStore';
import { useSearchStore } from '../../../stores/SearchStore';
import { useUserStore } from '../../../stores/UserStore';
import { useLookupStore } from '../../../stores/LookupStore';
import MissingImageInfoSnackbar from '../../../components/common/MissingImageInfoSnackbarComponent.vue';
import ImageDialog from '../../../components/dialog/ImageDialogComponent.vue';
import LoginDialog from '../../../components/dialog/LoginDialogComponent.vue';
import InfoDialogComponent from '@shared/components/InfoDialogComponent.vue';

export default {
  components: { MissingImageInfoSnackbar, ImageDialog, LoginDialog, InfoDialogComponent },

  data() {
    return {
      showImageDialog: false,
      showLoginDialog: false,
      showMissingCountryInfo: false,
      showNotJournalistInfo: false,
      selectedImage: undefined,
      downloadSelectedImage: undefined,
    };
  },

  computed: {
    ...mapStores(useImageStore, useSearchStore, useUserStore, useLookupStore),

    isAuthenticated() {
      return this.userStore.isAuthenticated;
    },

    isAuthenticatedAdmin() {
      return this.userStore.isAuthenticatedAdmin;
    },

    isAuthenticatedJournalist() {
      return this.userStore.isAuthenticatedJournalist;
    },

    countriesForSelectedImage() {
      if (!this.downloadSelectedImage?.company?.countries) return '';
      return this.lookupStore.countryNamesByIds(this.downloadSelectedImage?.company?.countries);
    },
  },

  methods: {
    enableDownloadImageTooltip(image) {
      // !isAuthenticated || (isAuthenticatedJournalist && isCountryMatch(image))
      return (
        this.isAuthenticated &&
        !this.isAuthenticatedAdmin &&
        (!this.isAuthenticatedJournalist || !this.isCountryMatch(image))
      );
    },

    imageClick(image) {
      this.selectedImage = image;
      this.showImageDialog = true;
    },
    hideImageDialog() {
      this.selectedImage = undefined;
      this.showImageDialog = false;
    },

    enabledDownloadButton(image) {
      return (
        !this.isAuthenticated ||
        this.isAuthenticatedAdmin ||
        (this.isAuthenticatedJournalist && this.isCountryMatch(image))
      );
    },

    isCountryMatch(image) {
      return this.userStore.hasMatchingCountry(image?.company?.countries);
    },

    downloadImage(image) {
      this.hideLoginDialog();

      if (!this.isAuthenticated) {
        this.downloadSelectedImage = image;
        this.showLoginDialog = true;
        return;
      }

      if (!this.isAuthenticatedJournalist && !this.isAuthenticatedAdmin) {
        this.showNotJournalistInfo = true;
        return;
      }

      const imageToDownload = image || this.downloadSelectedImage;
      const imageId = imageToDownload.id;
      const rnd = Math.floor(Math.random() * 100000000);

      if (!this.isCountryMatch(imageToDownload) && !this.isAuthenticatedAdmin) {
        this.showMissingCountryInfo = true;
        return;
      }

      window.open(`${API_URL}/image/download/${imageId}/${rnd}`, '_blank');
      this.downloadSelectedImage = undefined;
    },

    hideLoginDialog() {
      this.showLoginDialog = false;
    },
  },
};
</script>

<style lang="scss">
.thumb-image {
  width: 100%;
}
</style>
